import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {LoanApplicationService} from '../../../../service/loan-application.service';
import {LoanDetailsService} from '../../../../service/loan-details.service';
import {TileFactory} from '../tiles/tile-factory';
import {LoanTile} from '../tiles/tiles';
import {LoanStatusType} from '../../../../model/loan-status.model';
import {LoanApplicationPipelineStage, LoanApplicationTransition} from '../../../../model/LoanApplicationTransition';
import {LoanApplication} from '../../../../model/loan-application.model';
import {CommonUtil} from '../../../../utils/common.util';
import {Observable} from 'rxjs';
import {MatTableDataSource} from '@angular/material/table';
import {Loan} from '../../../../model/loan.model';
import {ChannelPartnerService} from '../../../../service/channel-partner.service';
import {ChannelPartner, ChannelPartnerGroupType} from '../../../../model/channel-partner.model';
import {SubventionUtil} from '../../../../utils/subvention-util';
import {LoanSubventionDetails} from '../../../../model/subvention-preference.model';
import {MathUtil} from '../../../../utils/math.util';

@Component({
  selector: 'app-all-in-progress-loan-applications',
  templateUrl: './all-in-progress-loan-applications.component.html',
  styleUrls: ['./all-in-progress-loan-applications.component.css', '../../../../../assets/styles/loan-tiles.scss']
})
export class AllInProgressLoanApplicationsComponent implements OnInit, OnDestroy {

  @Input()
  userId: string;
  loanTiles: LoanTile[];

  loanTilesObservables: Observable<any>;
  dataSource: MatTableDataSource<LoanTile>;
  defaultPageSize = 5;

  constructor(private loanApplicationService: LoanApplicationService, private loanDetailsService: LoanDetailsService,
              private channelPartnerService: ChannelPartnerService, private subventionUtil: SubventionUtil, private tileFactory: TileFactory) {
    this.dataSource =  new MatTableDataSource<LoanTile>();
  }

  async ngOnInit(): Promise<void> {
    const [loanApplications, loans] = await Promise.all([
      await this.loanApplicationService.getLoanApplicationsByUserId(this.userId),
      await this.loanDetailsService.getLoansByUserId(this.userId)
    ]);
    const sanctionedLoans = loans.filter(loan => loan.loan_sanction_status === LoanStatusType.Sanctioned);
    const allAtLeastSanctionedLoanApplicationIds: Set<string> = new Set<string>(loans.map(loan => loan.loanApplicationId));

    const yetToBeSanctionedLoanApplications = loanApplications.filter(loanApplication => !allAtLeastSanctionedLoanApplicationIds.has(loanApplication.id));
    const yetToBeAcceptedLoanApplicationIds = yetToBeSanctionedLoanApplications.map(loanApplication => loanApplication.id);
    yetToBeAcceptedLoanApplicationIds.push(...sanctionedLoans.map(loan => loan.loanApplicationId));

    const transitions: LoanApplicationTransition[] = await this.loanApplicationService.getAllTransitionsByLoanApplicationIds(yetToBeAcceptedLoanApplicationIds);
    const activeSubmittedButYetToBeSanctionedLoanApplications = this.findSubmittedButYetToBeSanctionedLoanApplications(yetToBeSanctionedLoanApplications, transitions);

    if (sanctionedLoans.length > 0 || activeSubmittedButYetToBeSanctionedLoanApplications.length > 0) {

      const partners = await this.getPartners(loanApplications);
      const partnersWithPartnerGroupsMapping = await this.setIfPartnerBelongsToSubventedPreApprovalPartnerGroup(partners);
      this.loanTiles = [];

      const loansSubventionDetails = sanctionedLoans.length > 0 ?  (await this.subventionUtil.getSubventionDetailsForLoans(sanctionedLoans)) : null;

      this.loanTiles.push(...this.constructTilesForLoans(sanctionedLoans, loanApplications, transitions, partnersWithPartnerGroupsMapping, loansSubventionDetails)
          .sort((firstLoanTile, secondLoanTile) =>
          new Date(firstLoanTile.applicationDate).getTime() - new Date(secondLoanTile.applicationDate).getTime()));

      this.loanTiles.push(...this.constructTilesForLoanApplications(activeSubmittedButYetToBeSanctionedLoanApplications, transitions, partnersWithPartnerGroupsMapping)
          .sort((firstLoanTile, secondLoanTile) =>
          new Date(secondLoanTile.applicationDate).getTime() - new Date(firstLoanTile.applicationDate).getTime()));
    } else {
      this.loanTiles = null;
    }
    this.dataSource.data = this.loanTiles;
    this.loanTilesObservables = this.dataSource.connect();
  }

  ngOnDestroy() {
    if (this.dataSource) {
      this.dataSource.disconnect();
    }
  }

  private findSubmittedButYetToBeSanctionedLoanApplications(yetToBeSanctionedLoanApplications: LoanApplication[],
                                                            transitions: LoanApplicationTransition[]): LoanApplication[] {
    const transitionsByLoanApplicationIds: Map<string, LoanApplicationTransition[]> = this.groupBy(transitions, transition => transition.entityId);
    return yetToBeSanctionedLoanApplications.filter(application => {
      const transitionsForLoanApplication = transitionsByLoanApplicationIds.get(application.id);
      const rejectedTransition = transitionsForLoanApplication ? transitionsForLoanApplication.find(t => t.entityId === application.id &&
          t.toStage === LoanApplicationPipelineStage.Rejection) : null;

      return !CommonUtil.isEmpty(transitionsForLoanApplication) && CommonUtil.isEmpty(rejectedTransition);
    });
  }

  private constructTilesForLoanApplications(loanApplications: LoanApplication[], transitions: LoanApplicationTransition[],
                                            partnersWithPartnerGroupsMapping: Map<string, boolean>): LoanTile[] | null {
    return loanApplications.map(loanApplication => {
      if (partnersWithPartnerGroupsMapping.get(loanApplication.partnerId)) {
        loanApplication.tenure = null;
      }
      const transitionsForLoanApplication = transitions.filter(transition => transition.entityId === loanApplication.id);
      return this.tileFactory.createForYetToBeSanctionedLoanApplication(loanApplication, transitionsForLoanApplication);
    });
  }

  private constructTilesForLoans(loans: Loan[], loanApplications: LoanApplication[], transitions: LoanApplicationTransition[],
                                 partnersWithPartnerGroupsMapping: Map<string, boolean>,
                                 loansSubventionDetailsForAllLoans: LoanSubventionDetails[]): LoanTile[] | null {
    return loans.map(loan => {
      const transitionsForLoanApplication = transitions.filter(transition => transition.entityId === loan.loanApplicationId);
      if (loansSubventionDetailsForAllLoans && loansSubventionDetailsForAllLoans.length > 0) {
        const loansSubventionDetails = loansSubventionDetailsForAllLoans.find(loansSubventionDetailsForLoan => loansSubventionDetailsForLoan.loanId === loan._id);
        if (loansSubventionDetails) {
          loan.effective_roi = MathUtil.roundOff(loansSubventionDetails.effectiveRoi);
        }
      }
      if (loan.partner) {
        const loanApplication = loanApplications.find(loanApp => loanApp.id === loan.loanApplicationId);
        if (partnersWithPartnerGroupsMapping.get(loanApplication.partnerId)) {
          loan.loan_tenure = null;
        }
      }
      return this.tileFactory.createForLoan(loan, transitionsForLoanApplication);
    });
  }

  private async getPartners(loanApplications: LoanApplication[]): Promise<ChannelPartner[]> {
    const partnerIds = Array.from(new Set<string>(loanApplications.filter(loanApplication => !CommonUtil.isEmpty(loanApplication.partnerId))
        .map(loanApplication => loanApplication.partnerId)));
    return partnerIds && partnerIds.length > 0 ? await this.channelPartnerService.getPartnerByIds(partnerIds) : [];
  }

  private async setIfPartnerBelongsToSubventedPreApprovalPartnerGroup(partners: ChannelPartner[]): Promise<Map<string, boolean>> {
    const map = new Map<string, boolean>();
    if (partners.length > 0) {
      const partnerGroup = await this.channelPartnerService.getChannelPartnerGroupByType(ChannelPartnerGroupType.SubventedPreApproval);
      partners.forEach(partner => {
        if (partner.partnerGroupIds && partner.partnerGroupIds.includes(partnerGroup._id)) {
          map.set(partner.id, true);
        } else {
          map.set(partner.id, false);
        }
      });
    }
    return map;
  }

  private groupBy(list, keyGetter): Map<string, LoanApplicationTransition[]> {
    const map = new Map<string, LoanApplicationTransition[]>();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }
}
